import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaSearch, FaShoppingCart, FaHeart, FaUser, FaBars, FaTimes,
  FaSun, FaMoon, FaGem, FaSignInAlt, FaSignOutAlt, FaUserCircle,
  FaShoppingBag, FaMapMarkerAlt
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useCart } from '../../context/CartContext';
import { useUser } from '../../context/UserContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  const { isDarkMode, toggleTheme } = useTheme();
  const { getCartItemsCount, setIsCartOpen, wishlistItems } = useCart();
  const { user, logout, isAuthenticated } = useUser();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    setIsUserDropdownOpen(false);
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Collections', path: '/collections' },
    { name: 'Gold', path: '/category/gold' },
    { name: 'Diamond', path: '/category/diamond' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <>
      <nav className={`navbar navbar-expand-lg ${isScrolled ? 'navbar-sticky' : ''} ${isDarkMode ? 'navbar-dark' : 'navbar-light'}`}
           style={{
             background: isScrolled
               ? (isDarkMode ? 'rgba(13, 13, 13, 0.95)' : 'rgba(255, 255, 255, 0.95)')
               : 'transparent',
             backdropFilter: isScrolled ? 'blur(10px)' : 'none',
             transition: 'all 0.3s ease',
             padding: isScrolled ? '10px 0' : '20px 0'
           }}>
        <div className="container">
          <Link to="/" className="navbar-brand d-flex align-items-center">
            <FaGem className="me-2" style={{ color: 'var(--primary-maroon)', fontSize: '28px' }} />
            <span style={{
              fontSize: '28px',
              fontFamily: 'var(--font-secondary)',
              fontWeight: '700',
              background: 'linear-gradient(135deg, var(--primary-maroon), var(--accent-gold))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Bharat Gold
            </span>
          </Link>

          {/* Desktop Search Bar */}
          <div className="d-none d-lg-flex flex-grow-1 mx-4">
            <div className="input-group" style={{ maxWidth: '500px', margin: '0 auto' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search for gold, diamond jewellery..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  borderRadius: '25px 0 0 25px',
                  border: '1px solid var(--primary-gold)',
                  paddingLeft: '20px',
                  background: isDarkMode ? 'var(--bg-secondary)' : 'white',
                  color: 'var(--text-primary)'
                }}
              />
              <button
                className="btn"
                style={{
                  borderRadius: '0 25px 25px 0',
                  background: 'var(--gradient-gold)',
                  border: '1px solid var(--primary-gold)',
                  color: '#1a1a1a'
                }}>
                <FaSearch />
              </button>
            </div>
          </div>

          {/* Desktop Menu Items */}
          <div className="d-none d-lg-flex align-items-center ms-auto">
            {navLinks.map((link, index) => (
              <Link
                key={index}
                to={link.path}
                className="nav-link mx-2"
                style={{
                  color: 'var(--text-primary)',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = 'var(--primary-gold)'}
                onMouseLeave={(e) => e.target.style.color = 'var(--text-primary)'}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Right Side Icons */}
          <div className="d-flex align-items-center ms-3">
            {/* Mobile Search Icon */}
            <button
              className="btn btn-link d-lg-none"
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              style={{ color: 'var(--text-primary)', fontSize: '20px' }}>
              <FaSearch />
            </button>

            {/* Theme Toggle */}
            <button
              className="btn btn-link"
              onClick={toggleTheme}
              style={{ color: 'var(--text-primary)', fontSize: '20px' }}>
              {isDarkMode ? <FaSun /> : <FaMoon />}
            </button>

            {/* Wishlist */}
            <Link to="/wishlist" className="btn btn-link position-relative"
                  style={{ color: 'var(--text-primary)', fontSize: '20px' }}>
              <FaHeart />
              {wishlistItems.length > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill"
                      style={{ background: 'var(--gradient-gold)', color: '#1a1a1a', fontSize: '10px' }}>
                  {wishlistItems.length}
                </span>
              )}
            </Link>

            {/* Cart */}
            <button
              className="btn btn-link position-relative"
              onClick={() => setIsCartOpen(true)}
              style={{ color: 'var(--text-primary)', fontSize: '20px' }}>
              <FaShoppingCart />
              {getCartItemsCount() > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill"
                      style={{ background: 'var(--gradient-gold)', color: '#1a1a1a', fontSize: '10px' }}>
                  {getCartItemsCount()}
                </span>
              )}
            </button>

            {/* User Dropdown */}
            <div className="dropdown">
              <button
                className="btn btn-link"
                onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                style={{ color: 'var(--text-primary)', fontSize: '20px' }}>
                <FaUser />
              </button>

              <AnimatePresence>
                {isUserDropdownOpen && (
                  <motion.div
                    className="dropdown-menu show position-absolute end-0"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    style={{
                      minWidth: '200px',
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '10px',
                      marginTop: '10px',
                      boxShadow: 'var(--shadow-lg)'
                    }}>
                    {isAuthenticated ? (
                      <>
                        <Link to="/profile" className="dropdown-item d-flex align-items-center">
                          <FaUserCircle className="me-2" /> Profile
                        </Link>
                        <Link to="/orders" className="dropdown-item d-flex align-items-center">
                          <FaShoppingBag className="me-2" /> My Orders
                        </Link>
                        <Link to="/addresses" className="dropdown-item d-flex align-items-center">
                          <FaMapMarkerAlt className="me-2" /> Addresses
                        </Link>
                        <div className="dropdown-divider"></div>
                        <button
                          onClick={handleLogout}
                          className="dropdown-item d-flex align-items-center text-danger">
                          <FaSignOutAlt className="me-2" /> Logout
                        </button>
                      </>
                    ) : (
                      <>
                        <Link to="/login" className="dropdown-item d-flex align-items-center">
                          <FaSignInAlt className="me-2" /> Login
                        </Link>
                        <Link to="/register" className="dropdown-item d-flex align-items-center">
                          <FaUserCircle className="me-2" /> Register
                        </Link>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="btn btn-link d-lg-none"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{ color: 'var(--text-primary)', fontSize: '24px' }}>
              {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

        {/* Mobile Search Bar */}
        <AnimatePresence>
          {isSearchOpen && (
            <motion.div
              className="container d-lg-none mt-3"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search jewellery..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    borderRadius: '25px 0 0 25px',
                    border: '1px solid var(--primary-gold)',
                    background: isDarkMode ? 'var(--bg-secondary)' : 'white'
                  }}
                />
                <button className="btn" style={{
                  borderRadius: '0 25px 25px 0',
                  background: 'var(--gradient-gold)',
                  color: '#1a1a1a'
                }}>
                  <FaSearch />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="mobile-menu d-lg-none"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              width: '80%',
              maxWidth: '300px',
              height: '100vh',
              background: 'var(--bg-primary)',
              zIndex: 1100,
              boxShadow: '-5px 0 15px rgba(0, 0, 0, 0.1)',
              overflowY: 'auto'
            }}>
            <div className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="gold-text mb-0">Menu</h5>
                <button
                  className="btn btn-link"
                  onClick={() => setIsMobileMenuOpen(false)}
                  style={{ color: 'var(--text-primary)', fontSize: '24px' }}>
                  <FaTimes />
                </button>
              </div>

              {navLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.path}
                  className="d-block py-3 text-decoration-none"
                  style={{
                    color: 'var(--text-primary)',
                    borderBottom: '1px solid var(--border-color)',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => setIsMobileMenuOpen(false)}>
                  {link.name}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overlay for mobile menu */}
      {isMobileMenuOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1099
          }}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>
  );
};

export default Navbar;